import { CaixaRosto, DimensoesVideo, ResultadoGateFacial } from './facial-gate';

export interface RegiaoRecorte {
  sx: number;
  sy: number;
  sw: number;
  sh: number;
}

export const MARGEM_EXIGIDA_PSIM = 0.25;

export const MARGEM_MINIMA_IMAGEM_CAPTURA = 0.15;

export const MARGEM_ALVO = 0.5;

export const ALTURA_MINIMA_ROSTO_PSIM = 0.1;

export const PROPORCAO_RECORTE = 2 / 3;

export function recorteFrameInteiro(video: DimensoesVideo): RegiaoRecorte {
  return { sx: 0, sy: 0, sw: video.width, sh: video.height };
}

export function calcularRecorteFacial(
  caixa: CaixaRosto | null | undefined,
  video: DimensoesVideo | null | undefined,
  margem: number = MARGEM_ALVO
): RegiaoRecorte | null {
  if (!video || video.width <= 0 || video.height <= 0) {
    return null;
  }

  if (!caixa || caixa.width <= 0 || caixa.height <= 0) {
    return recorteFrameInteiro(video);
  }

  const fator = 1 + 2 * margem;
  const larguraMinima = caixa.width * fator;
  const alturaMinima = caixa.height * fator;

  let sw = Math.max(larguraMinima, alturaMinima * PROPORCAO_RECORTE);
  let sh = sw / PROPORCAO_RECORTE;

  sw = Math.min(sw, video.width);
  sh = Math.min(sh, video.height);

  const centroX = caixa.x + caixa.width / 2;
  const centroY = caixa.y + caixa.height / 2;

  const sx = limitar(centroX - sw / 2, 0, video.width - sw);
  const sy = limitar(centroY - sh / 2, 0, video.height - sh);

  return {
    sx: Math.round(sx),
    sy: Math.round(sy),
    sw: Math.round(sw),
    sh: Math.round(sh)
  };
}

export function margemLateralEfetiva(regiao: RegiaoRecorte, caixa: CaixaRosto): number {
  if (!caixa || caixa.width <= 0) {
    return 0;
  }

  const folgaEsquerda = caixa.x - regiao.sx;
  const folgaDireita = regiao.sx + regiao.sw - (caixa.x + caixa.width);

  return Math.min(folgaEsquerda, folgaDireita) / caixa.width;
}

export function margemVerticalEfetiva(regiao: RegiaoRecorte, caixa: CaixaRosto): number {
  if (!caixa || caixa.height <= 0) {
    return 0;
  }

  const folgaAcima = caixa.y - regiao.sy;
  const folgaAbaixo = regiao.sy + regiao.sh - (caixa.y + caixa.height);

  return Math.min(folgaAcima, folgaAbaixo) / caixa.height;
}

export function atendeMargemPsim(regiao: RegiaoRecorte, caixa: CaixaRosto): boolean {
  return (
    margemLateralEfetiva(regiao, caixa) >= MARGEM_EXIGIDA_PSIM &&
    margemVerticalEfetiva(regiao, caixa) >= MARGEM_EXIGIDA_PSIM
  );
}

export function rostoAtendeAlturaMinimaPsim(
  caixa: CaixaRosto | null | undefined,
  video: DimensoesVideo | null | undefined
): boolean {
  if (!caixa || caixa.height <= 0 || !video || video.height <= 0) {
    return false;
  }
  return caixa.height >= video.height * ALTURA_MINIMA_ROSTO_PSIM;
}

export function rostoAtendeDistanciaCaptura(
  caixa: CaixaRosto | null | undefined,
  video: DimensoesVideo | null | undefined
): boolean {
  if (!caixa || caixa.width <= 0 || caixa.height <= 0 || !video || video.width <= 0 || video.height <= 0) {
    return false;
  }

  const margemHorizontal = video.width * MARGEM_MINIMA_IMAGEM_CAPTURA;
  const margemVertical = video.height * MARGEM_MINIMA_IMAGEM_CAPTURA;
  const folgaDireita = video.width - (caixa.x + caixa.width);
  const folgaAbaixo = video.height - (caixa.y + caixa.height);

  return caixa.x >= margemHorizontal && folgaDireita >= margemHorizontal &&
    caixa.y >= margemVertical && folgaAbaixo >= margemVertical;
}

export function recorteAtendeMargemPsim(
  caixa: CaixaRosto | null | undefined,
  video: DimensoesVideo | null | undefined
): boolean {
  if (!rostoAtendeAlturaMinimaPsim(caixa, video) || !rostoAtendeDistanciaCaptura(caixa, video)) {
    return false;
  }
  const regiao = calcularRecorteFacial(caixa, video);
  return !!regiao && atendeMargemPsim(regiao, caixa);
}

export function aplicarExigenciaMargemPsim(
  resultado: ResultadoGateFacial,
  caixa: CaixaRosto | null | undefined,
  video: DimensoesVideo | null | undefined
): ResultadoGateFacial {
  if (!resultado.liberado || recorteAtendeMargemPsim(caixa, video)) {
    return resultado;
  }
  return { ...resultado, liberado: false, motivo: 'AFASTE' };
}

function limitar(valor: number, minimo: number, maximo: number): number {
  if (maximo < minimo) {
    return minimo;
  }
  return Math.min(Math.max(valor, minimo), maximo);
}
